import React from "react";
import {
  Box,
  Grid,
  Typography,
  Container,
  Button,
  useTheme,
  Stack,
  Paper,
  Divider,
  Chip,
} from "@mui/material";
import CodeIcon from "@mui/icons-material/Code";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import VerifiedIcon from "@mui/icons-material/Verified";
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import PageTransition from "./PageTransition";

const pillars = [
  {
    title: "Innovation at Heart",
    description:
      "Design sprints and rapid prototyping that validate assumptions in days—not months—so your investments stay focused.",
    icon: <EmojiObjectsIcon />,
  },
  {
    title: "True Collaboration",
    description:
      "Embedded squads with shared OKRs, transparent tooling, and weekly governance create momentum and accountability.",
    icon: <GroupWorkIcon />,
  },
  {
    title: "Excellence Every Step",
    description:
      "ISO 27001-backed delivery, rigorous QA automation, and repeatable playbooks keep releases safe and predictable.",
    icon: <VerifiedIcon />,
  },
  {
    title: "Cutting-Edge Expertise",
    description:
      "Engineers fluent in AI, cloud-native, data mesh, and cybersecurity so each solution is built for tomorrow’s load.",
    icon: <CodeIcon />,
  },
];

const stats = [
  { label: "Net promoter score", value: "74", suffix: "" },
  { label: "Avg. go-live acceleration", value: "4x", suffix: "" },
  { label: "Strategic partners", value: "30+", suffix: "" },
];

const AboutUs = () => {
  const theme = useTheme();
  return (
    <Box component="section" id="about" sx={{ backgroundColor: "#f4f6fb", py: { xs: 8, md: 10 } }}>
      <PageTransition>
        <Container>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack spacing={2.5} alignItems="flex-start">
                <Chip
                  label="Who we are"
                  color="secondary"
                  sx={{
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    backgroundColor: "rgba(20,184,166,0.12)",
                    color: theme.palette.secondary.main,
                  }}
                />
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  Welcome to MC3 Smart Tech
                </Typography>
                <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.7 }}>
                  Founded in the UK, MC3 Smart Tech unites strategists, designers, engineers, and cybersecurity
                  specialists who build resilient platforms for ambitious organisations. We plug into your culture,
                  dismantle silos, and co-create measurable outcomes—from AI-enabled customer journeys to mission-critical
                  cloud infrastructure.
                </Typography>
              </Stack>
              <Stack direction="row" spacing={3} sx={{ mt: 4 }} flexWrap="wrap">
                {stats.map((stat) => (
                  <Box key={stat.label}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                      {stat.value}
                      {stat.suffix}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} sx={{ mt: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  href="/contact-us"
                  sx={{
                    background: "linear-gradient(120deg, #14b8a6, #0ea5e9)",
                    color: "#fff",
                    px: 4,
                  }}
                >
                  Book a workshop
                </Button>
                <Button variant="text" size="large" href="/our-services" sx={{ fontWeight: 600 }}>
                  Explore capabilities →
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box className="glass-panel" sx={{ p: { xs: 3, md: 4 } }}>
                <Stack spacing={3}>
                  {pillars.map((pillar) => (
                    <Paper
                      key={pillar.title}
                      elevation={0}
                      sx={{
                        p: 3,
                        border: "1px solid rgba(15,23,42,0.08)",
                        background: "white",
                        transition: "transform 0.3s ease, box-shadow 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 15px 45px rgba(15,23,42,0.08)",
                        },
                      }}
                    >
                      <Stack direction="row" spacing={2.5}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: "50%",
                            background: "linear-gradient(135deg, #f97316, #fb7185)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                          }}
                          aria-hidden
                        >
                          {pillar.icon}
                        </Box>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {pillar.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.7 }}>
                            {pillar.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </PageTransition>
    </Box>
  );
};

export default AboutUs;
